import { Link } from 'react-router'
import type { Alarm } from '../api/types'
import {
  type AlarmFilter, alarmCounts, alarmCountsText, alarmLink, alarmPill, alarmScopeLabel, alarmWhere, filterAlarms, levelWord,
} from '../lib/alerts'
import { MONITORING_DELAYED } from '../lib/freshness'
import { formatDateTime } from '../lib/time'

/** Who is dealing with an alarm and what they said, once anyone has said. */
function Acknowledged({ alarm }: { alarm: Alarm }) {
  const ack = alarm.acknowledged
  if (!ack) return null
  return <p className="alarm-ack"><strong>Acknowledged by {ack.by}, {formatDateTime(ack.at)}:</strong> {ack.note}</p>
}

/** Alarms (redesign phase 4, the Stations board): only alert rules raise
 *  them, and they are never the health monitor's system status. */

function where(alarm: Alarm): string {
  return alarm.parameter ? alarm.parameter.name : 'Station'
}

/** Home's Alarms card, beside System status. With the rule monitor quiet
 *  (`checked` false) it says the rules are not being checked, never None. */
export function AlarmsCard({ alarms, checked, error }: {
  alarms: Alarm[] | undefined; checked: boolean; error?: string
}) {
  if (alarms === undefined) {
    return (
      <div className="summary-card">
        <span className="summary-label">Alarms</span>
        <span className={`summary-note${error ? ' error' : ' muted'}`}>{error ?? 'Loading…'}</span>
      </div>
    )
  }
  if (!checked && !alarms.length) {
    return (
      <div className="summary-card alarms-card alarms-quiet">
        <span className="summary-label">Alarms</span>
        <span className="summary-value"><span className="summary-number">{MONITORING_DELAYED}</span></span>
        <span className="summary-note">The alert rules are not being checked right now.</span>
      </div>
    )
  }
  const c = alarmCounts(alarms)
  const tone = c.alarms ? 'alarm' : c.warnings ? 'warning' : 'quiet'
  return (
    <div className={`summary-card alarms-card alarms-${tone}`}>
      <span className="summary-label">Alarms</span>
      <span className="summary-value">
        <span className="summary-number">{alarms.length ? alarms.length : 'None'}</span>
      </span>
      <span className="summary-note">
        {alarms.length
          ? alarmCountsText(c)
          : 'No alarm is active'}
      </span>
    </div>
  )
}

/** Home's Alarms panel: a card per open alarm, filtered by Station or
 *  Channel. No graphic beside "None" and no set-up link (Tamar,
 *  2026-10-28): rules are set up on the Alarms page. */
export function AlarmsPanel({ alarms, filter, onFilter, checked, checkedAt }: {
  alarms: Alarm[]; filter: AlarmFilter; onFilter: (f: AlarmFilter) => void
  checked: boolean; checkedAt: string | null
}) {
  const c = alarmCounts(alarms)
  const shown = filterAlarms(alarms, filter)
  const n = { all: alarms.length, station: c.station, channel: c.channel }
  return (
    <section className="alarms-panel" aria-labelledby="alarms-head">
      <div className="alarms-panel-head">
        <h2 id="alarms-head">Alarms</h2>
        <div className="alarms-panel-tools">
          <div className="segmented" role="group" aria-label="Alarms on">
            {(['all', 'station', 'channel'] as const).map((key) => (
              <button key={key} type="button" aria-pressed={filter === key} onClick={() => onFilter(key)}>
                {{ all: 'All', station: 'Station', channel: 'Channel' }[key]}{' '}
                <span className="count">({n[key]})</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {!checked && (
        <div className="notice station-alert status-stale al-pad" role="status">
          <p className="station-alert-head">
            {checkedAt
              ? `The alert rules have not been checked since ${formatDateTime(checkedAt)}.`
              : 'The alert rules have not been checked yet.'}
            {' '}The alarms below may not be current; the rule monitor runs in ingest-slow.
          </p>
        </div>
      )}
      {shown.length ? (
        <div className="alarm-cards">
          {shown.map((a) => (
            <div key={a.id} className={`alarm-card alarm-${a.level}`}>
              <div className="alarm-card-head">
                <span className={`alarm-level alarm-level-${a.level}`}><span className="al-dot" />{levelWord(a.level)}</span>
                <span className="alarm-rule">{a.rule.name}</span>
                <span className="muted">at</span>
                <Link className="alarm-station" to={alarmLink(a)}>{alarmWhere(a)}</Link>
              </div>
              <div className="alarm-card-where">
                <span className="alarm-chip">{alarmScopeLabel(a)}</span>
                {a.parameter && <span className="mono">{a.parameter.name}</span>}
              </div>
              {a.rule.message && <p className="alarm-text">{a.rule.message}</p>}
              <span className="alarm-since mono">Open since {formatDateTime(a.since)}</span>
              <Acknowledged alarm={a} />
            </div>
          ))}
        </div>
      ) : (
        <div className="alarms-none">
          <span>
            <strong>None.</strong>{' '}
            {alarms.length && filter !== 'all' ? `No ${filter} alarm is active.`
              : checked ? 'No alarm is active.' : 'No alarm is known to be active.'}
          </span>
        </div>
      )}
    </section>
  )
}

/** The Active alarms cell of Home's table. */
export function AlarmCell({ alarms }: { alarms: Alarm[] }) {
  if (!alarms.length) return <span className="muted">None</span>
  return (
    <span className="alarm-cell">
      {alarms.map((a) => (
        <span key={a.id} className="alarm-cell-item">
          <span className={`alarm-level alarm-level-${a.level}`}><span className="al-dot" />{levelWord(a.level)}</span>
          <span className="st-sub">{where(a)}</span>
        </span>
      ))}
    </span>
  )
}

/** The pill a station header or project card carries: n alarms or
 *  warnings, or none. */
export function AlarmPill({ alarms, none = 'Alarms: none' }: { alarms: Alarm[]; none?: string }) {
  const pill = alarmPill(alarms)
  return (
    <span className={`pill ${pill.tone === 'none' ? 'status-stale' : `status-${pill.tone}`}`}>
      {pill.tone === 'none' ? none : pill.text}
    </span>
  )
}

/** The open alarms listed under a station's header, as the board does. */
export function StationAlarms({ alarms }: { alarms: Alarm[] }) {
  if (!alarms.length) return null
  return (
    <ul className="station-alarms">
      {alarms.map((a) => (
        <li key={a.id} className={`alarm-${a.level}`}>
          <span className={`alarm-level alarm-level-${a.level}`}><span className="al-dot" />{levelWord(a.level)}</span>
          <strong>{a.rule.name}</strong>
          <span className="muted">{where(a)}</span>
          {a.rule.message && <span>{a.rule.message}</span>}
          <span className="muted mono">since {formatDateTime(a.since)}</span>
          <Acknowledged alarm={a} />
        </li>
      ))}
    </ul>
  )
}
